import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { LogoIcon } from "@/components/Icon";
import { AreaMultiSelect } from "@/components/AreaMultiSelect";
import { ApiError, apiPostJson, apiPatchJson, apiJson } from "@/lib/api";
import { getApiBase } from "@/lib/config";
import { setAuthUser, getAuthUser, type AuthUser } from "@/lib/auth";
import { sanitizeNext, resolveLanding } from "@/lib/authLanding";
import { STORAGE_ONBOARDING } from "@/constants/storage";

// ─── Types ────────────────────────────────────────────────────────────────────
//
// One screen state-machine drives the page. Routes into specific entry
// points (welcome / signin / signup / forgot / reset / verify) come from
// `?reset_token=`, `?verify_token=`, `?oauth_result=`, or `?error=`.
//
// New since v1:
//   - "forgot"     — email entry for password reset
//   - "forgot-sent" — confirmation that the reset email is on its way
//   - "reset"      — set new password from a reset link
//   - "verify"     — after clicking the verify-email link in inbox

type Screen =
  | "welcome" | "signin" | "signup"
  | "forgot" | "forgot-sent" | "reset"
  | "verify"
  | "role" | "household" | "partner" | "agent-cea" | "seller-profile"
  | "preferences" | "done";

type Role = "buyer" | "seller" | "agent" | "browser" | null;
type HouseholdType = "solo" | "couple" | null;

// ─── Password strength ────────────────────────────────────────────────────────
//
// Mirrors the server's validPassword (len ≥ 8 AND has digit OR symbol),
// extended with a 0–4 score for the meter. Keeping it here (vs zxcvbn) keeps
// the bundle small; the server is still authoritative.
export function passwordScore(pw: string): { score: 0 | 1 | 2 | 3 | 4; valid: boolean } {
  if (!pw) return { score: 0, valid: false };
  const len = pw.length;
  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /[0-9]/.test(pw);
  // PROP-203: mirror the server's ASCII-only symbol ranges
  // (auth_password.go validPassword). The old /[^A-Za-z0-9]/ also matched
  // non-ASCII glyphs (£, €, …), so the FE called a password valid that the
  // server then rejected with 400.
  const hasSymbol = [...pw].some((ch) => {
    const c = ch.codePointAt(0) ?? 0;
    return (c >= 0x21 && c <= 0x2f) || (c >= 0x3a && c <= 0x40) || (c >= 0x5b && c <= 0x60) || (c >= 0x7b && c <= 0x7e);
  });
  const variety = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;

  let score: 0 | 1 | 2 | 3 | 4 = 0;
  if (len >= 8) score = 1;
  if (len >= 8 && variety >= 2) score = 2;
  if (len >= 10 && variety >= 3) score = 3;
  if (len >= 12 && variety >= 3) score = 4;

  const valid = len >= 8 && (hasDigit || hasSymbol);
  return { score, valid };
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function Logo() {
  const { t, i18n } = useTranslation();
  const toggleLang = () => {
    const next = i18n.language?.startsWith("zh") ? "en" : "zh";
    void i18n.changeLanguage(next);
  };
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <LogoIcon size={30} />
        <div className="font-serif text-xl font-semibold tracking-tight text-text leading-none">
          ProProp<span className="text-accent">.</span>
        </div>
      </div>
      <button
        type="button"
        onClick={toggleLang}
        title={t("common.switchLanguage")}
        className="rounded-md border border-border px-2 py-[3px] text-[11px] font-medium text-text-muted transition hover:border-border-strong hover:text-text"
      >
        {i18n.language?.startsWith("zh") ? "EN" : "中"}
      </button>
    </div>
  );
}

function BackBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 bg-transparent border-none cursor-pointer text-xs text-text-muted hover:text-text transition-colors p-0"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {label}
    </button>
  );
}

function ProgressDots({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="h-1 rounded-full transition-all duration-200"
          style={{
            width: i === current ? 20 : 6,
            background: i <= current ? "var(--accent)" : "var(--color-border-strong)",
          }}
        />
      ))}
    </div>
  );
}

interface FieldProps {
  label: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  error?: string;
  autoFocus?: boolean;
  rightSlot?: React.ReactNode;
}

function Field({ label, type = "text", placeholder, value, onChange, hint, error, autoFocus, rightSlot }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-semibold uppercase tracking-widest text-text-muted">
        {label}
      </label>
      <div className="relative">
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          className={`input-field ${error ? "!border-[color:var(--color-bad,#7a2d1f)]" : ""} ${rightSlot ? "pr-16" : ""}`}
          style={error ? { fontSize: 16, borderColor: "#7a2d1f" } : { fontSize: 16 }}
        />
        {rightSlot && (
          <div className="absolute inset-y-0 right-2 flex items-center">{rightSlot}</div>
        )}
      </div>
      {(hint || error) && (
        <p
          className="text-[11.5px] font-serif italic leading-snug m-0"
          style={{ color: error ? "#7a2d1f" : "var(--color-text-muted)" }}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
}

// PasswordField — Field with built-in show/hide toggle and (optional) strength meter.
interface PwFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  showStrength?: boolean;
  error?: string;
  hint?: string;
  autoFocus?: boolean;
}

function PasswordField({ label, value, onChange, placeholder, showStrength, error, hint, autoFocus }: PwFieldProps) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  const meta = passwordScore(value);
  const meterLabel =
    meta.score <= 1 ? t("auth.passwordWeak") :
    meta.score === 2 ? t("auth.passwordOk") :
    meta.score === 3 ? t("auth.passwordGood") :
                       t("auth.passwordStrong");
  const meterColor =
    meta.score <= 1 ? "#7a2d1f" :
    meta.score === 2 ? "#a87a26" :
    meta.score === 3 ? "var(--accent)" :
                       "var(--accent)";

  return (
    <div>
      <Field
        label={label}
        type={shown ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
        error={error}
        hint={hint}
        rightSlot={
          <button
            type="button"
            onClick={() => setShown((s) => !s)}
            className="bg-transparent border-none cursor-pointer text-[11px] font-medium text-text-muted hover:text-text px-1"
            tabIndex={-1}
          >
            {shown ? t("auth.passwordHide") : t("auth.passwordShow")}
          </button>
        }
      />
      {showStrength && value && (
        <div className="mt-1.5 flex items-center gap-2">
          <div className="flex flex-1 gap-0.5">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-[3px] flex-1 rounded-full transition-colors"
                style={{ background: i <= meta.score ? meterColor : "var(--color-border)" }}
              />
            ))}
          </div>
          <span className="text-[10.5px] font-medium" style={{ color: meterColor }}>
            {meterLabel}
          </span>
        </div>
      )}
    </div>
  );
}

interface BtnProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "ghost";
  full?: boolean;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit";
}

function Btn({
  children, onClick, variant = "primary",
  full = false, disabled = false, size = "md", type = "button",
}: BtnProps) {
  const pad =
    size === "lg" ? "px-6 py-3" :
    size === "sm" ? "px-3.5 py-1.5" : "px-5 py-2.5";
  const fs =
    size === "lg" ? "text-[15px]" :
    size === "sm" ? "text-xs" : "text-[13.5px]";
  const variantCls = {
    primary: "bg-accent text-bg hover:bg-accent-hover",
    secondary: "bg-transparent text-text-sec border border-border-strong hover:bg-surface-2 hover:text-text",
    ghost: "bg-transparent text-text-muted hover:text-accent",
  }[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold tracking-tight transition-all duration-150 ${pad} ${fs} ${variantCls} ${full ? "w-full" : ""} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-px bg-border" />
      <span className="text-[11px] text-text-dim">{label}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function SocialBtn() {
  const { t } = useTranslation();
  // Forward `next` and infer `intent` from where the user originated.
  // RequirePro encodes the pre-auth path into ?next=/pro/... — when we
  // see that we tell the backend `intent=pro` so a brand-new Google
  // user gets role=pro at INSERT and an existing ordinary user gets
  // upgraded. Without this hint the backend defaults to ordinary and
  // the user gets bounced back to /chat post-login.
  const params = new URLSearchParams(window.location.search);
  const next = params.get("next") || "";
  const wantsPro = next.startsWith("/pro/") || next === "/pro";
  const oauthQS = new URLSearchParams();
  if (wantsPro) oauthQS.set("intent", "pro");
  if (next) oauthQS.set("next", next);
  const qs = oauthQS.toString();
  const href = qs
    ? `${getApiBase()}/auth/google?${qs}`
    : `${getApiBase()}/auth/google`;
  return (
    <a
      href={href}
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-surface px-3 py-2.5 text-[12.5px] font-medium text-text-sec transition hover:border-border-strong hover:bg-surface-2"
    >
      <GoogleIcon />
      <span>{t("auth.continueWithGoogle")}</span>
    </a>
  );
}

// ─── Layout shell ─────────────────────────────────────────────────────────────

function Shell({ children, aside }: { children: React.ReactNode; aside: React.ReactNode }) {
  return (
    <div className="flex h-[100dvh] w-full flex-col md:flex-row overflow-hidden bg-bg">
      <div className="scrollbar-thin flex flex-1 flex-col items-center overflow-y-auto px-5 py-10 sm:px-10 md:px-12">
        <div className="w-full max-w-[420px]">
          <div className="mb-9">
            <Logo />
          </div>
          {children}
          <div className="h-10" />
        </div>
      </div>
      <div className="hidden md:flex w-[480px] shrink-0 flex-col items-center justify-center px-10 py-12 border-l border-border bg-surface relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.35,
            backgroundImage:
              "linear-gradient(var(--color-border) 1px, transparent 1px), linear-gradient(90deg, var(--color-border) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="relative z-10 w-full max-w-[360px]">{aside}</div>
      </div>
    </div>
  );
}

// ─── Aside content ────────────────────────────────────────────────────────────

function AsideChat() {
  const { t } = useTranslation();
  const tags = [t("auth.aside.tagURA"), t("auth.aside.tagIRAS"), t("auth.aside.tagDataGov")];
  return (
    <div>
      <p className="font-serif text-[22px] font-medium tracking-tight text-text leading-tight mb-2 m-0" style={{ textWrap: "balance" } as React.CSSProperties}>
        {t("auth.aside.headline")}
      </p>
      <p className="font-serif italic text-sm text-text-muted mb-7 leading-relaxed mt-2 m-0">
        {t("auth.aside.sub")}
      </p>

      <div className="flex flex-col gap-2.5">
        <div className="rounded-[10px] px-3 py-2.5 bg-accent-dim text-text text-[13px] leading-relaxed">
          {t("auth.aside.exampleQ")}
        </div>
        <div
          className="px-3 py-2.5 text-text font-serif text-[13.5px] leading-relaxed"
          style={{
            background: "var(--ai-bg)",
            borderLeft: "3px solid var(--ai-rule)",
            borderRadius: "3px 10px 10px 3px",
          }}
        >
          {t("auth.aside.exampleA")}
        </div>
        <div
          className="px-3 py-2.5 text-[10.5px] font-semibold tracking-wider"
          style={{
            border: "1px dashed var(--ai-rule)",
            borderRadius: 8,
            color: "var(--ai-accent)",
          }}
        >
          {t("auth.aside.exampleCite")}
        </div>
      </div>
      <div className="mt-7 flex gap-2.5 items-center text-[10.5px] text-text-dim flex-wrap">
        {tags.map((label) => (
          <span key={label} className="flex items-center gap-1">
            <span className="w-[5px] h-[5px] rounded-full inline-block bg-accent" />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

const ROLE_ICONS: Record<string, string> = {
  buyer: "🏠",
  seller: "📋",
  agent: "🤝",
};

function AsideRole({ role }: { role: Role }) {
  const { t } = useTranslation();
  if (!role || !ROLE_ICONS[role]) return <AsideChat />;
  const title = t(`auth.roleFeatures.${role}.title`);
  const points = t(`auth.roleFeatures.${role}.points`, { returnObjects: true }) as string[];
  return (
    <div>
      <div className="text-[32px] mb-3">{ROLE_ICONS[role]}</div>
      <p className="font-serif text-[22px] font-medium tracking-tight text-text leading-tight mb-5 m-0">
        {title}
      </p>
      <div className="flex flex-col gap-2.5">
        {points.map((p) => (
          <div key={p} className="flex gap-2.5 items-start">
            <div
              className="w-[18px] h-[18px] rounded-full shrink-0 flex items-center justify-center mt-0.5"
              style={{ background: "var(--accent-dim)" }}
            >
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                <path d="M2 5.5l2 2 4-4" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="text-[13px] text-text-sec leading-snug m-0">{p}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Role selection card ──────────────────────────────────────────────────────

interface RoleCardProps {
  icon: string;
  title: string;
  desc: string;
  selected: boolean;
  onSelect: () => void;
}

function RoleCard({ icon, title, desc, selected, onSelect }: RoleCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full text-left cursor-pointer rounded-[10px] px-4 py-3.5 border flex gap-3.5 items-start transition-all duration-150"
      style={{
        background: selected ? "var(--accent-dim)" : "var(--color-surface)",
        borderColor: selected ? "var(--accent)" : "var(--color-border)",
      }}
    >
      <div
        className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center text-[17px] transition-all duration-150"
        style={{ background: selected ? "var(--accent)" : "var(--color-surface-2)" }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-semibold tracking-tight mb-0.5"
          style={{ color: selected ? "var(--accent)" : "var(--color-text)" }}
        >
          {title}
        </div>
        <div className="font-serif italic text-[12.5px] text-text-muted leading-snug">{desc}</div>
      </div>
      <div
        className="w-[18px] h-[18px] rounded-full shrink-0 flex items-center justify-center mt-0.5 transition-all duration-150 border"
        style={{
          borderColor: selected ? "var(--accent)" : "var(--color-border-strong)",
          background: selected ? "var(--accent)" : "transparent",
        }}
      >
        {selected && (
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
            <path d="M2 5.5l2 2 4-4" stroke="var(--color-bg)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
    </button>
  );
}

// ─── Chip ─────────────────────────────────────────────────────────────────────

function Chip({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-[7px] rounded-full border text-[12.5px] transition-all duration-150"
      style={{
        borderColor: selected ? "var(--accent)" : "var(--color-border)",
        background: selected ? "var(--accent-dim)" : "var(--color-surface)",
        color: selected ? "var(--accent)" : "var(--color-text-sec)",
        fontWeight: selected ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

// ─── Error mapping ────────────────────────────────────────────────────────────
//
// The backend speaks plain English. Translate well-known messages into our
// i18n keys so users see localised copy. Fallback: show the raw message if
// it doesn't match a known pattern (still better than nothing in dev).
function mapApiError(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return t("auth.errorRateLimit");
    const m = (err.message || "").toLowerCase();
    if (m.includes("invalid email or password")) return t("auth.errorBadCreds");
    if (m.includes("already registered") || m.includes("email already")) return t("auth.errorEmailTaken");
    if (m.includes("password must be")) return t("auth.errorWeakPassword");
    if (m.includes("invalid email")) return t("auth.errorInvalidEmail");
  }
  return t("auth.errorGeneric");
}

// ─── Screen: Welcome ──────────────────────────────────────────────────────────

function WelcomeScreen({ onSignIn, onSignUp }: { onSignIn: () => void; onSignUp: () => void }) {
  const { t } = useTranslation();
  return (
    <Shell aside={<AsideChat />}>
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="font-serif text-[34px] font-medium tracking-[-0.03em] leading-[1.1] text-text m-0">
            {t("auth.tagline")}
          </h1>
          <p className="font-serif italic text-[15px] text-text-muted mt-2.5 leading-relaxed m-0">
            {t("auth.taglineSub")}
          </p>
        </div>
        <div className="flex flex-col gap-2.5">
          <Btn full size="lg" onClick={onSignUp}>{t("auth.getStarted")}</Btn>
          <Btn full size="lg" variant="secondary" onClick={onSignIn}>{t("auth.signIn")}</Btn>
        </div>
        <p className="text-[11px] text-text-dim leading-relaxed border-t border-border pt-4 m-0">
          <Trans
            i18nKey="auth.termsNotice"
            components={{ terms: <span className="underline cursor-pointer" />, privacy: <span className="underline cursor-pointer" /> }}
          />
        </p>
      </div>
    </Shell>
  );
}

// ─── Screen: Sign in ──────────────────────────────────────────────────────────

function SignInScreen({
  onBack, onDone, onSignUp, onForgot, loading, apiError,
}: {
  onBack: () => void;
  onDone: (email: string, password: string) => void;
  onSignUp: () => void;
  onForgot: () => void;
  loading: boolean;
  apiError: string | null;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !pw) { setError(t("auth.fillBoth")); return; }
    setError("");
    onDone(email, pw);
  };

  return (
    <Shell aside={<AsideChat />}>
      <BackBtn onClick={onBack} label={t("common.back")} />
      <div className="mb-7 mt-7">
        <h2 className="font-serif text-[28px] font-medium tracking-tight text-text m-0">{t("auth.welcomeBack")}</h2>
        <p className="font-serif italic text-sm text-text-muted mt-1.5 m-0">{t("auth.welcomeBackSub")}</p>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <SocialBtn />
        <Divider label={t("auth.orWithEmail")} />
        <Field
          label={t("auth.email")} type="email" placeholder={t("auth.emailPlaceholder")}
          value={email} onChange={setEmail} autoFocus
        />
        <div>
          <PasswordField
            label={t("auth.password")}
            placeholder={t("auth.passwordPlaceholder")}
            value={pw} onChange={setPw}
            error={apiError ?? error ?? undefined}
          />
          <button
            type="button"
            onClick={onForgot}
            className="text-[11.5px] mt-1.5 px-0 bg-transparent border-none cursor-pointer underline underline-offset-2"
            style={{ color: "var(--ai-accent)", textDecorationColor: "var(--ai-rule)" }}
          >
            {t("auth.forgotPassword")}
          </button>
        </div>
        <Btn full size="lg" type="submit" disabled={loading}>
          {loading ? t("auth.submitting") : t("auth.signIn")}
        </Btn>
      </form>
      <p className="text-center mt-5 text-[12.5px] text-text-muted m-0">
        {t("auth.noAccount")}{" "}
        <button
          type="button"
          onClick={onSignUp}
          className="bg-transparent border-none cursor-pointer text-accent font-semibold text-[12.5px] p-0"
        >
          {t("auth.signUp")}
        </button>
      </p>
    </Shell>
  );
}

// ─── Screen: Sign up ──────────────────────────────────────────────────────────

function SignUpScreen({
  onBack, onNext, onSignIn, loading, apiError,
}: {
  onBack: () => void;
  onNext: (name: string, email: string, password: string) => void;
  onSignIn: () => void;
  loading: boolean;
  apiError: string | null;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const meta = passwordScore(pw);
  const valid = email.includes("@") && meta.valid && name.length > 1;

  return (
    <Shell aside={<AsideChat />}>
      <BackBtn onClick={onBack} label={t("common.back")} />
      <div className="mb-6 mt-7">
        <h2 className="font-serif text-[28px] font-medium tracking-tight text-text m-0">{t("auth.createAccount")}</h2>
        <p className="font-serif italic text-sm text-text-muted mt-1.5 m-0">{t("auth.createAccountSub")}</p>
      </div>
      <div className="flex flex-col gap-4">
        <SocialBtn />
        <Divider label={t("auth.orSignUpWithEmail")} />
        <Field label={t("auth.fullName")} placeholder={t("auth.namePlaceholder")} value={name} onChange={setName} autoFocus />
        <Field label={t("auth.email")} type="email" placeholder={t("auth.emailPlaceholder")} value={email} onChange={setEmail} />
        <PasswordField
          label={t("auth.password")} placeholder={t("auth.passwordPlaceholderMin")}
          value={pw} onChange={setPw}
          showStrength
          hint={apiError ?? t("auth.passwordHint")}
          error={apiError ?? undefined}
        />
        <Btn full size="lg" disabled={!valid || loading} onClick={() => onNext(name, email, pw)}>
          {loading ? t("auth.creating") : t("auth.continueBtn")}
        </Btn>
      </div>
      <p className="text-center mt-5 text-[12.5px] text-text-muted m-0">
        {t("auth.haveAccount")}{" "}
        <button
          type="button"
          onClick={onSignIn}
          className="bg-transparent border-none cursor-pointer text-accent font-semibold text-[12.5px] p-0"
        >
          {t("auth.signIn")}
        </button>
      </p>
    </Shell>
  );
}

// ─── Screen: Forgot password ──────────────────────────────────────────────────

function ForgotScreen({ onBack, onSent, apiError, loading, onSubmit }: {
  onBack: () => void;
  onSent: () => void;
  apiError: string | null;
  loading: boolean;
  onSubmit: (email: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const valid = email.includes("@");

  return (
    <Shell aside={<AsideChat />}>
      <BackBtn onClick={onBack} label={t("auth.forgot.back")} />
      <div className="mb-6 mt-7">
        <h2 className="font-serif text-[28px] font-medium tracking-tight text-text m-0">{t("auth.forgot.title")}</h2>
        <p className="font-serif italic text-sm text-text-muted mt-1.5 m-0">{t("auth.forgot.sub")}</p>
      </div>
      <div className="flex flex-col gap-4">
        <Field
          label={t("auth.email")} type="email" placeholder={t("auth.emailPlaceholder")}
          value={email} onChange={setEmail} autoFocus error={apiError ?? undefined}
        />
        <Btn full size="lg" disabled={!valid || loading}
          onClick={async () => { await onSubmit(email); onSent(); }}
        >
          {loading ? t("auth.forgot.submitting") : t("auth.forgot.submit")}
        </Btn>
      </div>
    </Shell>
  );
}

function ForgotSentScreen({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <Shell aside={<AsideChat />}>
      <BackBtn onClick={onBack} label={t("auth.forgot.back")} />
      <div className="mb-6 mt-7">
        <h2 className="font-serif text-[28px] font-medium tracking-tight text-text m-0">{t("auth.forgot.title")}</h2>
        <p className="font-serif italic text-sm text-text-muted mt-1.5 m-0">{t("auth.forgot.sent")}</p>
      </div>
      <Btn full size="lg" variant="secondary" onClick={onBack}>{t("auth.forgot.back")}</Btn>
    </Shell>
  );
}

// ─── Screen: Reset password (from email link) ─────────────────────────────────

function ResetScreen({
  token, loading, apiError, expired, onSubmit, onRequestNew,
}: {
  token: string;
  loading: boolean;
  apiError: string | null;
  expired: boolean;
  onSubmit: (newPw: string) => void;
  onRequestNew: () => void;
}) {
  const { t } = useTranslation();
  const [pw, setPw] = useState("");
  const meta = passwordScore(pw);

  if (expired) {
    return (
      <Shell aside={<AsideChat />}>
        <div className="mb-6 mt-7">
          <h2 className="font-serif text-[28px] font-medium tracking-tight text-text m-0">{t("auth.reset.title")}</h2>
          <p className="font-serif italic text-sm text-text-muted mt-1.5 m-0">{t("auth.reset.linkExpired")}</p>
        </div>
        <Btn full size="lg" onClick={onRequestNew}>{t("auth.reset.requestNew")}</Btn>
      </Shell>
    );
  }

  return (
    <Shell aside={<AsideChat />}>
      <div className="mb-6 mt-7">
        <h2 className="font-serif text-[28px] font-medium tracking-tight text-text m-0">{t("auth.reset.title")}</h2>
        <p className="font-serif italic text-sm text-text-muted mt-1.5 m-0">{t("auth.reset.sub")}</p>
      </div>
      <div className="flex flex-col gap-4">
        <PasswordField
          label={t("auth.reset.newPassword")} placeholder={t("auth.passwordPlaceholderMin")}
          value={pw} onChange={setPw} showStrength autoFocus
          error={apiError ?? undefined}
        />
        <Btn full size="lg" disabled={!meta.valid || loading || !token}
          onClick={() => onSubmit(pw)}
        >
          {loading ? t("auth.reset.submitting") : t("auth.reset.submit")}
        </Btn>
      </div>
    </Shell>
  );
}

// ─── Screen: Verify email confirmation ────────────────────────────────────────

function VerifyScreen({ status, onContinue, onRequestNew }: {
  status: "loading" | "success" | "expired";
  onContinue: () => void;
  onRequestNew: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Shell aside={<AsideChat />}>
      <div className="mb-6 mt-7">
        <h2 className="font-serif text-[28px] font-medium tracking-tight text-text m-0">
          {status === "success" ? t("auth.verify.success")
            : status === "expired" ? t("auth.verify.linkExpired")
            : t("common.loading")}
        </h2>
      </div>
      {status === "success" && (
        <Btn full size="lg" onClick={onContinue}>{t("auth.continueBtn")}</Btn>
      )}
      {status === "expired" && (
        <Btn full size="lg" onClick={onRequestNew}>{t("auth.signIn")}</Btn>
      )}
    </Shell>
  );
}

// ─── Screen: Role selection ───────────────────────────────────────────────────

function RoleScreen({ onBack, onNext }: { onBack: () => void; onNext: (r: Role) => void }) {
  const { t } = useTranslation();
  const [role, setRole] = useState<Role>(null);

  return (
    <Shell aside={<AsideRole role={role} />}>
      <div className="mb-1.5 flex items-center justify-between">
        <BackBtn onClick={onBack} label={t("common.back")} />
        <ProgressDots total={3} current={0} />
      </div>
      <div className="my-5">
        <h2 className="font-serif text-[26px] font-medium tracking-tight text-text leading-tight m-0">
          {t("auth.role.title")}
        </h2>
        <p className="font-serif italic text-[13.5px] text-text-muted mt-1.5 leading-relaxed m-0">
          {t("auth.role.sub")}
        </p>
      </div>
      <div className="flex flex-col gap-2.5 mb-6">
        <RoleCard icon="🏠" title={t("auth.role.buyer")} desc={t("auth.role.buyerDesc")}
          selected={role === "buyer"} onSelect={() => setRole("buyer")} />
        <RoleCard icon="📋" title={t("auth.role.seller")} desc={t("auth.role.sellerDesc")}
          selected={role === "seller"} onSelect={() => setRole("seller")} />
        <RoleCard icon="🤝" title={t("auth.role.agent")} desc={t("auth.role.agentDesc")}
          selected={role === "agent"} onSelect={() => setRole("agent")} />
        <RoleCard icon="👀" title={t("auth.role.browser")} desc={t("auth.role.browserDesc")}
          selected={role === "browser"} onSelect={() => setRole("browser")} />
      </div>
      <Btn full size="lg" disabled={!role} onClick={() => role && onNext(role)}>{t("auth.continueBtn")}</Btn>
    </Shell>
  );
}

// ─── Screen: Household setup ──────────────────────────────────────────────────

function HouseholdScreen({ onBack, onNext }: { onBack: () => void; onNext: (t: HouseholdType) => void }) {
  const { t } = useTranslation();
  const [type, setType] = useState<HouseholdType>(null);

  return (
    <Shell aside={<AsideRole role="buyer" />}>
      <div className="mb-1.5 flex items-center justify-between">
        <BackBtn onClick={onBack} label={t("common.back")} />
        <ProgressDots total={3} current={1} />
      </div>
      <div className="my-5">
        <h2 className="font-serif text-[26px] font-medium tracking-tight text-text leading-tight m-0">
          {t("auth.household.title")}
        </h2>
        <p className="font-serif italic text-[13.5px] text-text-muted mt-1.5 leading-relaxed m-0">
          {t("auth.household.sub")}
        </p>
      </div>
      <div className="flex flex-col gap-2.5 mb-6">
        <RoleCard icon="👤" title={t("auth.household.solo")} desc={t("auth.household.soloDesc")}
          selected={type === "solo"} onSelect={() => setType("solo")} />
        <RoleCard icon="👫" title={t("auth.household.couple")} desc={t("auth.household.coupleDesc")}
          selected={type === "couple"} onSelect={() => setType("couple")} />
      </div>
      <Btn full size="lg" disabled={!type} onClick={() => type && onNext(type)}>{t("auth.continueBtn")}</Btn>
    </Shell>
  );
}

// ─── Screen: Partner invite ───────────────────────────────────────────────────

function PartnerInviteScreen({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  return (
    <Shell aside={<AsideRole role="buyer" />}>
      <div className="mb-1.5 flex items-center justify-between">
        <BackBtn onClick={onBack} label={t("common.back")} />
        <ProgressDots total={3} current={2} />
      </div>
      <div className="my-5">
        <h2 className="font-serif text-[26px] font-medium tracking-tight text-text leading-tight m-0">
          {t("auth.partner.title")}
        </h2>
        <p className="font-serif italic text-[13.5px] text-text-muted mt-1.5 leading-relaxed m-0">
          {t("auth.partner.sub")}
        </p>
      </div>

      {!sent ? (
        <div className="flex flex-col gap-3.5">
          <Field
            label={t("auth.partner.emailLabel")} type="email"
            placeholder={t("auth.partner.emailPlaceholder")}
            value={email} onChange={setEmail} autoFocus hint={t("auth.partner.hint")}
          />
          <Btn full size="lg" disabled={!email.includes("@")} onClick={() => setSent(true)}>
            {t("auth.partner.send")}
          </Btn>
          <Btn full variant="ghost" onClick={onNext}>{t("auth.partner.skip")}</Btn>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div
            className="flex gap-3 rounded-[10px] px-4 py-4"
            style={{ background: "var(--accent-dim)", border: "1px solid rgba(29,74,58,0.22)" }}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="shrink-0 mt-0.5">
              <circle cx="10" cy="10" r="9" stroke="var(--accent)" strokeWidth="1.5" />
              <path d="M6 10.5l2.5 2.5L14 8" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-accent m-0">{t("auth.partner.sentTitle", { email })}</p>
              <p className="font-serif italic text-[12.5px] text-text-muted mt-1 leading-snug m-0">
                {t("auth.partner.sentBody")}
              </p>
            </div>
          </div>
          <Btn full size="lg" onClick={onNext}>{t("auth.partner.continue")}</Btn>
        </div>
      )}
    </Shell>
  );
}

// ─── Screen: Agent CEA verification ──────────────────────────────────────────

interface CEAValidateResponse {
  valid: boolean;
  active: boolean;
  salesperson_name?: string;
  estate_agent_name?: string;
  estate_agent_license_no?: string;
  registration_start_date?: string;
  registration_end_date?: string;
  reason?: string; // "not_found" | "expired"
}

function AgentVerifyScreen({ onBack, onNext }: { onBack: () => void; onNext: (cea: string, agency: string) => void }) {
  const { t } = useTranslation();
  const [cea, setCea] = useState("");
  const [agency, setAgency] = useState("");
  const [checked, setChecked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifiedName, setVerifiedName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-typing the number invalidates the prior verification — prevents
  // the user from passing CEA-A, then editing the field to CEA-B and
  // continuing without re-checking.
  const setCeaInput = (v: string) => {
    setCea(v.toUpperCase());
    if (verified) {
      setVerified(false);
      setVerifiedName(null);
    }
    if (error) setError(null);
  };

  const handleVerify = async () => {
    if (cea.length < 6 || verifying) return;
    setVerifying(true);
    setError(null);
    try {
      const res = await apiPostJson<CEAValidateResponse>("/auth/cea/validate", { cea_number: cea });
      if (!res.valid) {
        setError(t("auth.agentVerify.errors.notFound"));
        return;
      }
      if (!res.active) {
        setError(t("auth.agentVerify.errors.expired"));
        return;
      }
      setVerified(true);
      setVerifiedName(res.salesperson_name || null);
      // Pre-fill agency from the registry — user can still override.
      if (res.estate_agent_name && !agency) {
        setAgency(res.estate_agent_name);
      }
    } catch (e) {
      setError(e instanceof ApiError && e.status === 429
        ? t("auth.agentVerify.errors.rateLimited")
        : t("auth.agentVerify.errors.network"));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Shell aside={<AsideRole role="agent" />}>
      <div className="mb-1.5 flex items-center justify-between">
        <BackBtn onClick={onBack} label={t("common.back")} />
        <ProgressDots total={3} current={1} />
      </div>
      <div className="my-5">
        <h2 className="font-serif text-[26px] font-medium tracking-tight text-text leading-tight m-0">
          {t("auth.agentVerify.title")}
        </h2>
        <p className="font-serif italic text-[13.5px] text-text-muted mt-1.5 leading-relaxed m-0">
          {t("auth.agentVerify.sub")}
        </p>
      </div>

      <div className="flex flex-col gap-3.5 mb-5">
        <div className="flex gap-2.5 items-end">
          <div className="flex-1">
            <Field
              label={t("auth.agentVerify.ceaLabel")} placeholder={t("auth.agentVerify.ceaPlaceholder")}
              value={cea} onChange={setCeaInput} autoFocus
              error={error ?? undefined}
            />
          </div>
          <Btn
            onClick={handleVerify}
            disabled={cea.length < 6 || verifying || verified}
            variant={verified ? "secondary" : "primary"}
          >
            {verifying ? t("auth.agentVerify.verifying") : verified ? t("auth.agentVerify.verified") : t("auth.agentVerify.verify")}
          </Btn>
        </div>

        {verified && (
          <div
            className="rounded-lg px-3.5 py-3"
            style={{ background: "var(--accent-dim)", border: "1px solid rgba(29,74,58,0.22)" }}
          >
            <p className="text-sm font-semibold text-accent m-0">{t("auth.agentVerify.verifiedTitle", { cea })}</p>
            {verifiedName && (
              <p className="text-[12.5px] text-text-sec mt-0.5 m-0">{verifiedName}</p>
            )}
            <p className="font-serif italic text-[12.5px] text-text-muted mt-0.5 m-0">
              {t("auth.agentVerify.verifiedSub")}
            </p>
          </div>
        )}

        <Field
          label={t("auth.agentVerify.agencyLabel")} placeholder={t("auth.agentVerify.agencyPlaceholder")}
          value={agency} onChange={setAgency}
        />

        <div
          className="rounded-lg px-3.5 py-3"
          style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-rule)" }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider mb-1.5 m-0" style={{ color: "var(--ai-accent)" }}>
            {t("auth.agentVerify.whyTitle")}
          </p>
          <p className="font-serif italic text-[12.5px] text-text-sec leading-relaxed m-0">
            {t("auth.agentVerify.whyBody")}
          </p>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 cursor-pointer accent-accent shrink-0"
          />
          <span className="text-xs text-text-sec leading-relaxed">
            <Trans i18nKey="auth.agentVerify.consent" components={{ terms: <span className="underline" /> }} />
          </span>
        </label>
      </div>

      <Btn full size="lg" disabled={!verified || !agency || !checked} onClick={() => onNext(cea, agency)}>
        {t("auth.agentVerify.continue")}
      </Btn>
    </Shell>
  );
}

// ─── Screen: Seller profile ───────────────────────────────────────────────────

function SellerProfileScreen({
  onBack,
  onNext,
}: {
  onBack: () => void;
  // Lifts (propType, timeline) so the parent can seed the default workspace
  // (intent="sell", timeline=...). The fields themselves are not persisted
  // to auth_users — they live on the workspace.
  onNext: (propType: string, timeline: string) => void;
}) {
  const { t } = useTranslation();
  const [propType, setPropType] = useState("");
  const [timeline, setTimeline] = useState("");

  const types: Array<{ key: string; label: string }> = [
    { key: "hdb", label: t("auth.seller.types.hdb") },
    { key: "private", label: t("auth.seller.types.private") },
    { key: "landed", label: t("auth.seller.types.landed") },
    { key: "ec", label: t("auth.seller.types.ec") },
    { key: "unsure", label: t("auth.seller.types.unsure") },
  ];
  const timelines: Array<{ key: string; label: string }> = [
    { key: "soon", label: t("auth.seller.timelines.soon") },
    { key: "mid", label: t("auth.seller.timelines.mid") },
    { key: "later", label: t("auth.seller.timelines.later") },
    { key: "exploring", label: t("auth.seller.timelines.exploring") },
  ];

  return (
    <Shell aside={<AsideRole role="seller" />}>
      <div className="mb-1.5 flex items-center justify-between">
        <BackBtn onClick={onBack} label={t("common.back")} />
        <ProgressDots total={3} current={1} />
      </div>
      <div className="my-5">
        <h2 className="font-serif text-[26px] font-medium tracking-tight text-text leading-tight m-0">
          {t("auth.seller.title")}
        </h2>
        <p className="font-serif italic text-[13.5px] text-text-muted mt-1.5 leading-relaxed m-0">
          {t("auth.seller.sub")}
        </p>
      </div>

      <div className="flex flex-col gap-5 mb-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-text-muted mb-2 m-0">
            {t("auth.seller.type")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {types.map((it) => (
              <Chip key={it.key} label={it.label} selected={propType === it.key} onClick={() => setPropType(it.key)} />
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-text-muted mb-2 m-0">
            {t("auth.seller.timeline")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {timelines.map((it) => (
              <Chip key={it.key} label={it.label} selected={timeline === it.key} onClick={() => setTimeline(it.key)} />
            ))}
          </div>
        </div>
      </div>

      <Btn full size="lg" disabled={!propType || !timeline} onClick={() => onNext(propType, timeline)}>
        {t("auth.seller.continue")}
      </Btn>
    </Shell>
  );
}

// ─── Screen: Preferences ──────────────────────────────────────────────────────
//
// Collected for buyer flow (and seller flow piggy-backs via seller-profile).
// These map to the user's default workspace, NOT to auth_users — they seed
// budget/areas/timeline/priorities so the workspace lands with a useful
// shortlist context instead of being blank.

interface WorkspacePrefs {
  intent?: string;            // "buy" | "sell" | "rent" | "explore"
  budget_min?: number | null;
  budget_max?: number | null;
  areas?: string;             // comma-separated regions
  timeline?: string;          // bucketed string, e.g. "0-3mo"
  priorities?: string[];      // ["mrt", "schools", ...]
}

// Budget bucket → (min, max). max=null means "no upper bound".
// Kept as discrete buckets (vs free-form sliders) because users rarely
// know exact figures at signup time, and aggregate buckets are easier
// to seed comp queries with later.
const BUDGET_BUCKETS: Record<string, { min: number; max: number | null }> = {
  under800k:    { min: 0,       max: 800_000 },
  "800k-1.2m":  { min: 800_000, max: 1_200_000 },
  "1.2m-1.8m":  { min: 1_200_000, max: 1_800_000 },
  "1.8m-3m":    { min: 1_800_000, max: 3_000_000 },
  over3m:       { min: 3_000_000, max: null },
};

function PreferencesScreen({
  onBack,
  onNext,
}: {
  onBack: () => void;
  onNext: (prefs: WorkspacePrefs) => void;
}) {
  const { t } = useTranslation();
  const [budget, setBudget] = useState<string>("");
  const [areas, setAreas] = useState<string[]>([]);
  const [timeline, setTimeline] = useState<string>("");
  const [priorities, setPriorities] = useState<Set<string>>(new Set());

  const budgetOpts = Object.keys(BUDGET_BUCKETS) as Array<keyof typeof BUDGET_BUCKETS>;
  const timelineOpts = ["0-3mo", "3-6mo", "6-12mo", "exploring"];
  const priorityOpts = ["mrt", "schools", "size", "newLaunch", "quiet", "yield"];

  const toggleSet = (s: Set<string>, v: string) => {
    const next = new Set(s);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    return next;
  };

  const submit = () => {
    const bucket = budget ? BUDGET_BUCKETS[budget] : null;
    onNext({
      intent: "buy",
      budget_min: bucket?.min ?? null,
      budget_max: bucket?.max ?? null,
      areas: areas.join(","),
      timeline,
      priorities: Array.from(priorities),
    });
  };

  // All steps optional — user may "Skip" through. Encourages signup completion.
  // We persist a "skipped" flag so the workspace can show a soft CTA later
  // inviting the user to fill in prefs (without nagging users who entered
  // some fields). Best-effort: ignore localStorage failures (private mode).
  const skip = () => {
    try { window.localStorage.setItem(STORAGE_ONBOARDING.prefsSkipped, "1"); } catch { /* ignore */ }
    onNext({ intent: "buy" });
  };

  return (
    <Shell aside={<AsideRole role="buyer" />}>
      <div className="mb-1.5 flex items-center justify-between">
        <BackBtn onClick={onBack} label={t("common.back")} />
        <ProgressDots total={3} current={2} />
      </div>
      <div className="my-5">
        <h2 className="font-serif text-[26px] font-medium tracking-tight text-text leading-tight m-0">
          {t("auth.prefs.title")}
        </h2>
        <p className="font-serif italic text-[13.5px] text-text-muted mt-1.5 leading-relaxed m-0">
          {t("auth.prefs.sub")}
        </p>
      </div>

      <div className="flex flex-col gap-5 mb-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-text-muted mb-2 m-0">
            {t("auth.prefs.budget")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {budgetOpts.map((k) => (
              <Chip
                key={k}
                label={t(`auth.prefs.budgets.${k}`)}
                selected={budget === k}
                onClick={() => setBudget(budget === k ? "" : k)}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-text-muted mb-2 m-0">
            {t("auth.prefs.areas")}
          </p>
          <AreaMultiSelect value={areas} onChange={setAreas} />
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-text-muted mb-2 m-0">
            {t("auth.prefs.timeline")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {timelineOpts.map((k) => (
              <Chip
                key={k}
                label={t(`auth.prefs.timelineOpts.${k}`)}
                selected={timeline === k}
                onClick={() => setTimeline(timeline === k ? "" : k)}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-text-muted mb-2 m-0">
            {t("auth.prefs.priorities")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {priorityOpts.map((k) => (
              <Chip
                key={k}
                label={t(`auth.prefs.priorityOpts.${k}`)}
                selected={priorities.has(k)}
                onClick={() => setPriorities((s) => toggleSet(s, k))}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <Btn full variant="secondary" size="lg" onClick={skip}>
            {t("auth.prefs.skip")}
          </Btn>
        </div>
        <div className="flex-1">
          <Btn full size="lg" onClick={submit}>
            {t("auth.prefs.continue")} →
          </Btn>
        </div>
      </div>
    </Shell>
  );
}

// ─── Screen: Done ─────────────────────────────────────────────────────────────

interface PendingProfile {
  role?: string;
  household_type?: string;
  cea_number?: string;
  agency?: string;
}

// DoneScreen used to PATCH /auth/profile every time it mounted, even on
// re-login (when pendingProfile was empty). Now we:
//   - skip the PATCH when pendingProfile is empty (post-signin path)
//   - surface failures as a one-time error banner instead of swallowing them
//   - additionally seed the user's default workspace with collected
//     preferences (budget/areas/timeline/priorities) — best-effort, a
//     workspace PATCH failure does NOT block onboarding completion.
function DoneScreen({
  role,
  pendingProfile,
  pendingPrefs,
  onGo,
}: {
  role: string;
  pendingProfile: PendingProfile;
  pendingPrefs: WorkspacePrefs | null;
  onGo: () => void;
}) {
  const { t } = useTranslation();
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const hasAny = Object.values(pendingProfile).some((v) => v !== undefined && v !== "");
    if (hasAny) {
      const backendRole = pendingProfile.role === "agent" ? "pro" : "ordinary";
      apiPatchJson<{ user: AuthUser }>("/auth/profile", { ...pendingProfile, role: backendRole })
        .then(({ user }) => {
          if (user) setAuthUser(user);
        })
        .catch(() => setSaveError(t("auth.errorGeneric")));
    }

    // Seed default workspace with preferences (if any). Two-step:
    // 1. GET /workspaces — backend lazy-creates the default if missing.
    // 2. PATCH /workspaces/{id} with non-empty prefs.
    // We deliberately do NOT block onGo on this — the user's default
    // workspace will still exist (just without seeded prefs) if this fails.
    //
    // C4: agents (role === "agent") are routed past PreferencesScreen,
    // so pendingPrefs is null on this path. /pro/* uses its own data
    // model (CRM / brand / pitch) and does NOT use /workspace, so even
    // if a stray pendingPrefs value reached us we'd skip the seed below.
    const isAgent = pendingProfile.role === "agent";
    if (pendingPrefs && !isAgent) {
      const hasPrefs =
        pendingPrefs.budget_min != null ||
        pendingPrefs.budget_max != null ||
        (pendingPrefs.areas && pendingPrefs.areas.length > 0) ||
        (pendingPrefs.timeline && pendingPrefs.timeline.length > 0) ||
        (pendingPrefs.priorities && pendingPrefs.priorities.length > 0) ||
        (pendingPrefs.intent && pendingPrefs.intent.length > 0);
      if (hasPrefs) {
        apiJson<{ workspaces: Array<{ id: string }> }>("/workspaces")
          .then(({ workspaces }) => {
            const wsId = workspaces?.[0]?.id;
            if (!wsId) throw new Error("no workspace");
            return apiPatchJson(`/workspaces/${wsId}`, pendingPrefs);
          })
          .catch(() => {
            // Non-fatal but user-visible. Surface a one-time nudge banner
            // on the workspace next visit so the user can re-enter prefs.
            // eslint-disable-next-line no-console
            console.warn("workspace prefs seed failed (non-fatal)");
            try {
              window.localStorage.setItem(STORAGE_ONBOARDING.prefsSeedFailed, "1");
            } catch {
              /* private mode — banner just won't show */
            }
          });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const k = role.startsWith("buyer") ? role : (role || "buyer_solo");
  const h = t(`auth.done.${k}.h`);
  const sub = t(`auth.done.${k}.sub`);
  const cta = t(`auth.done.${k}.cta`);
  const displayRole: Role = role?.startsWith("buyer") ? "buyer" : role as Role;

  return (
    <Shell aside={<AsideRole role={displayRole} />}>
      <div className="flex flex-col gap-6">
        <div
          className="w-[52px] h-[52px] rounded-full flex items-center justify-center"
          style={{ background: "var(--accent-dim)", border: "1px solid rgba(29,74,58,0.22)" }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M5 12.5l4.5 4.5L19 7" stroke="var(--accent)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <h2 className="font-serif text-[34px] font-medium tracking-[-0.03em] leading-[1.1] text-text m-0">{h}</h2>
          <p className="font-serif italic text-[15px] text-text-muted mt-2.5 leading-relaxed max-w-[380px] m-0">{sub}</p>
        </div>

        {saveError && (
          <div className="rounded-lg px-3.5 py-2.5 text-[12px]" style={{ background: "rgba(122,45,31,0.1)", color: "#7a2d1f" }}>
            {saveError}
          </div>
        )}

        <div
          className="rounded-lg px-3.5 py-3 font-serif italic text-xs leading-relaxed"
          style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", color: "var(--ai-accent)" }}
        >
          {t("auth.done.disclosure")}
        </div>

        <Btn full size="lg" onClick={onGo}>{cta} →</Btn>
      </div>
    </Shell>
  );
}

// ─── Root: Auth ────────────────────────────────────────────────────────────────

export function Auth() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Determine the *initial* screen from URL params before useState fires.
  // This handles three distinct entry-from-email cases:
  //   ?reset_token=…   → show ResetScreen
  //   ?verify_token=…  → show VerifyScreen (auto-confirms on mount)
  //   ?oauth_result=…  → handled by effect (existing flow)
  const initial = useMemo<{ screen: Screen; resetToken?: string; verifyToken?: string }>(() => {
    if (typeof window === "undefined") return { screen: "welcome" };
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset_token")) {
      return { screen: "reset", resetToken: params.get("reset_token")! };
    }
    if (params.get("verify_token")) {
      return { screen: "verify", verifyToken: params.get("verify_token")! };
    }
    return { screen: "welcome" };
  }, []);

  const [screen, setScreen] = useState<Screen>(initial.screen);
  const [role, setRole] = useState<Role>(null);
  const [householdType, setHouseholdType] = useState<HouseholdType>(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [pendingProfile, setPendingProfile] = useState<PendingProfile>({});
  // Workspace seed — lives separately from PendingProfile because it targets
  // a different endpoint (/workspaces vs /auth/profile) and a different
  // resource shape.
  const [pendingPrefs, setPendingPrefs] = useState<WorkspacePrefs | null>(null);

  // Reset / verify state
  const [resetToken] = useState<string>(initial.resetToken ?? "");
  const [resetExpired, setResetExpired] = useState(false);
  const [verifyToken] = useState<string>(initial.verifyToken ?? "");
  const [verifyStatus, setVerifyStatus] = useState<"loading" | "success" | "expired">("loading");
  // PROP-171: capture a safe post-auth landing path from ?next= ONCE at
  // mount, so every email/password landing point (sign-in, onboarding
  // finish, password reset, email-verify, cached-session bounce) honours
  // deep links the same way the OAuth-return effect already does — and so
  // it survives the history.replaceState("/auth") the OAuth/verify
  // branches run. Re-validated (starts "/" not "//") against tampering.
  const [safeNext] = useState<string>(() =>
    sanitizeNext(new URLSearchParams(window.location.search).get("next")),
  );
  // landingFor binds the shared resolveLanding (lib/authLanding) to this
  // mount's safeNext, so every email/password landing point resolves the
  // deep link the same way the OAuth-return effect does (PROP-171).
  const landingFor = (role: string | null | undefined): string =>
    resolveLanding(safeNext, role);

  // Handle OAuth redirect back from Google.
  // Also auto-confirm the verify token if we landed on /auth?verify_token=…
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthResult = params.get("oauth_result");
    const oauthError = params.get("error");
    // `next` is set by the backend callback when authGoogleStart saw a
    // safe ?next= path. It overrides the default role-based landing so
    // a user who started at /pro/dashboard lands back there instead of
    // the role-default /pro/dashboard or /chat. Re-validated here
    // (must start with "/" and not "//") in case anyone tries to
    // tamper with the URL after the FE bundle loads.
    // safeNext (the deep link from ?next=) is captured once at mount
    // above (PROP-171); reuse that single validated value here rather
    // than re-reading params, so the OAuth and email paths agree.

    // Verify-email flow: POST the token, flip the status.
    if (verifyToken) {
      apiPostJson<{ user: AuthUser }>("/auth/verify/confirm", { token: verifyToken })
        .then(({ user }) => {
          if (user) setAuthUser(user);
          setVerifyStatus("success");
        })
        .catch(() => setVerifyStatus("expired"));
      // Clean the URL so refresh doesn't re-POST.
      window.history.replaceState({}, "", "/auth");
    }

    if (!oauthResult && !oauthError) return;
    window.history.replaceState({}, "", "/auth");
    if (oauthError) {
      // PROP-207: surface the specific "already linked elsewhere" reason
      // the backend reports, instead of a generic failure for every case.
      setApiError(
        oauthError === "oauth_link_conflict"
          ? (t("auth.errorOauthLinkConflict", {
              defaultValue: "This Google account is already linked to another ProProp account.",
            }) as string)
          : t("auth.errorOauth"),
      );
      return;
    }
    apiJson<{ user: AuthUser }>("/auth/me")
      .then(({ user }) => {
        setAuthUser(user);
        if (oauthResult === "onboard" || !user.role) {
          // Brand-new Google user OR existing user who never finished onboarding.
          // Skip the RoleScreen if the backend already set role=pro
          // via intent (route-1 product spec): the entry path already
          // declared what they wanted, no need to re-ask.
          if (user.role === "pro") {
            navigate(safeNext || "/pro/dashboard");
          } else {
            setScreen("role");
          }
        } else if (safeNext && (user.role === "pro" || !safeNext.startsWith("/pro"))) {
          // Respect next when it's reachable by this user. Block the
          // pathological case of a buyer-role user being sent to /pro/*
          // (RequirePro would just bounce them again).
          navigate(safeNext);
        } else {
          navigate(user.role === "pro" ? "/pro/dashboard" : "/chat");
        }
      })
      .catch(() => setApiError(t("auth.errorOauth")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock body scroll — same pattern as AppShellConsumer
  useEffect(() => {
    const prev = {
      overflow: document.body.style.overflow,
      overscrollBehavior: document.body.style.overscrollBehavior,
      position: document.body.style.position,
      width: document.body.style.width,
      top: document.body.style.top,
      left: document.body.style.left,
    };
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = "0";
    document.body.style.left = "0";
    return () => { Object.assign(document.body.style, prev); };
  }, []);

  // Half-onboarded recovery: if we boot at /auth and the user is already
  // signed in but has no role (e.g. closed the tab during onboarding),
  // jump straight back to "role" instead of the Welcome screen.
  useEffect(() => {
    if (initial.screen !== "welcome") return; // we already routed via token
    const cached = getAuthUser();
    if (cached && !cached.role) {
      setScreen("role");
    } else if (cached && cached.role) {
      // Fully onboarded — bounce to the right home, honouring a deep
      // link in ?next= when present (PROP-171).
      navigate(landingFor(cached.role), { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (s: Screen) => { setApiError(null); setScreen(s); };

  const handleSignIn = async (email: string, password: string) => {
    setLoading(true); setApiError(null);
    try {
      const { user } = await apiPostJson<{ user: AuthUser }>("/auth/signin", { email, password });
      setAuthUser(user);
      // If the existing account has no role (shouldn't happen with new signup,
      // but old accounts pre-onboarding may), drop into onboarding.
      if (!user.role) goTo("role");
      // PROP-171: an already-onboarded user signing in should resume
      // their deep link (safeNext) when present, not be parked on the
      // onboarding "done" screen that lands on /chat.
      else navigate(landingFor(user.role));
    } catch (err) {
      setApiError(mapApiError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (name: string, email: string, password: string) => {
    setLoading(true); setApiError(null);
    try {
      const { user } = await apiPostJson<{ user: AuthUser }>("/auth/signup", { email, password, name });
      setAuthUser(user);
      goTo("role");
    } catch (err) {
      setApiError(mapApiError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (email: string) => {
    setLoading(true); setApiError(null);
    try {
      await apiPostJson("/auth/password/forgot", { email });
    } catch (err) {
      // Per the privacy contract, this endpoint always returns 200 — but
      // 429 from the rate limiter is real. Surface that one.
      if (err instanceof ApiError && err.status === 429) {
        setApiError(t("auth.errorRateLimit"));
        throw err;
      }
      // ignore other errors — UX is "we'll email if it exists"
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (newPw: string) => {
    setLoading(true); setApiError(null);
    try {
      const { user } = await apiPostJson<{ user: AuthUser }>("/auth/password/reset", {
        token: resetToken, new_password: newPw,
      });
      setAuthUser(user);
      // PROP-171: honour ?next= after a password reset too.
      navigate(landingFor(user.role));
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && (err.message || "").toLowerCase().includes("expired")) {
        setResetExpired(true);
      } else {
        setApiError(mapApiError(err, t));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoleNext = (r: Role) => {
    setRole(r);
    setPendingProfile(prev => ({ ...prev, role: r ?? undefined }));
    if (r === "buyer") goTo("household");
    else if (r === "agent") goTo("agent-cea");
    else if (r === "browser") goTo("done");
    else goTo("seller-profile");
  };

  const handleHouseholdNext = (tt: HouseholdType) => {
    setHouseholdType(tt);
    setPendingProfile(prev => ({ ...prev, household_type: tt ?? undefined }));
    // Both solo and couple buyers go through preferences before done.
    // The partner-invite step (couple only) has been folded INTO the
    // preferences screen — the original partner-invite screen is now
    // skipped because nothing was actually persisted from it. We keep
    // the screen in the state machine for backwards compat with any
    // OAuth flow that lands users in the middle.
    if (tt === "couple") goTo("partner");
    else goTo("preferences");
  };

  const handleGo = () => {
    // PROP-171: after onboarding completes, resume the deep link in
    // ?next= when reachable; otherwise the role home.
    navigate(landingFor(role));
  };

  const doneRole =
    role === "buyer"
      ? householdType === "couple" ? "buyer" : "buyer_solo"
      : role ?? "buyer_solo";

  return (
    <>
      {screen === "welcome" && (
        <WelcomeScreen onSignIn={() => goTo("signin")} onSignUp={() => goTo("signup")} />
      )}
      {screen === "signin" && (
        <SignInScreen
          onBack={() => goTo("welcome")}
          onDone={handleSignIn}
          onSignUp={() => goTo("signup")}
          onForgot={() => goTo("forgot")}
          loading={loading}
          apiError={apiError}
        />
      )}
      {screen === "signup" && (
        <SignUpScreen
          onBack={() => goTo("welcome")}
          onNext={handleSignUp}
          onSignIn={() => goTo("signin")}
          loading={loading}
          apiError={apiError}
        />
      )}
      {screen === "forgot" && (
        <ForgotScreen
          onBack={() => goTo("signin")}
          onSent={() => goTo("forgot-sent")}
          apiError={apiError}
          loading={loading}
          onSubmit={handleForgot}
        />
      )}
      {screen === "forgot-sent" && (
        <ForgotSentScreen onBack={() => goTo("signin")} />
      )}
      {screen === "reset" && (
        <ResetScreen
          token={resetToken}
          loading={loading}
          apiError={apiError}
          expired={resetExpired}
          onSubmit={handleReset}
          onRequestNew={() => { setResetExpired(false); goTo("forgot"); }}
        />
      )}
      {screen === "verify" && (
        <VerifyScreen
          status={verifyStatus}
          onContinue={() => {
            const u = getAuthUser();
            // PROP-171: resume ?next= after email verification too.
            navigate(landingFor(u?.role), { replace: true });
          }}
          onRequestNew={() => goTo("signin")}
        />
      )}
      {screen === "role" && (
        <RoleScreen onBack={() => goTo("signup")} onNext={handleRoleNext} />
      )}
      {screen === "household" && (
        <HouseholdScreen onBack={() => goTo("role")} onNext={handleHouseholdNext} />
      )}
      {screen === "partner" && (
        <PartnerInviteScreen onBack={() => goTo("household")} onNext={() => goTo("preferences")} />
      )}
      {screen === "agent-cea" && (
        <AgentVerifyScreen
          onBack={() => goTo("role")}
          onNext={(cea, agency) => {
            setPendingProfile(prev => ({ ...prev, cea_number: cea, agency }));
            goTo("done");
          }}
        />
      )}
      {screen === "seller-profile" && (
        <SellerProfileScreen
          onBack={() => goTo("role")}
          onNext={(propType, timeline) => {
            // Seller flow: seed workspace with sell intent + timeline.
            // propType is captured but not yet mapped (no workspace
            // field for it currently — could become priorities[] later).
            setPendingPrefs({
              intent: "sell",
              timeline,
              priorities: propType ? [propType] : [],
            });
            goTo("done");
          }}
        />
      )}
      {screen === "preferences" && (
        <PreferencesScreen
          onBack={() => goTo(role === "buyer" ? (householdType === "couple" ? "partner" : "household") : "role")}
          onNext={(prefs) => {
            setPendingPrefs(prefs);
            goTo("done");
          }}
        />
      )}
      {screen === "done" && (
        <DoneScreen
          role={doneRole}
          pendingProfile={pendingProfile}
          pendingPrefs={pendingPrefs}
          onGo={handleGo}
        />
      )}
    </>
  );
}
